import axios from 'axios'

const API_BASE = '/api/quant'
const LOCAL_STORAGE_KEY = 'quant_data'
const CURRENT_FILE_KEY = 'current_data_file'
const USER_KEY = 'simutrade_user'

const api = axios.create({
  baseURL: API_BASE,
  timeout: 60000
})

function getCurrentUserId() {
  try {
    const userStr = localStorage.getItem(USER_KEY)
    if (userStr) {
      const user = JSON.parse(userStr)
      return user.id || 1
    }
  } catch (error) {
  }
  return 1
}

export const quantApi = {
  async getFileList() {
    const userId = getCurrentUserId()
    const response = await api.get('/files', {
      params: { user_id: userId }
    })
    return response.data
  },

  async getDataFile(filename, options = {}) {
    const { offset = 0, limit = 10000, sample = false, period = null } = options
    const userId = getCurrentUserId()

    let targetFilename = filename
    if (period) {
      targetFilename = this.buildFilenameWithPeriod(filename, period)
    }

    const files = await this.getFileList()
    const file = files.files.find(f => f.filename === targetFilename)
    const useSample = sample || (file && file.size_bytes > 10 * 1024 * 1024)

    const response = await api.get(`/data/${targetFilename}`, {
      params: {
        offset: offset,
        limit: useSample ? 1000 : limit,
        sample: useSample,
        user_id: userId
      }
    })

    const data = response.data
    if (offset === 0) {
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(data))
      localStorage.setItem(CURRENT_FILE_KEY, targetFilename)
    }

    return data
  },

  buildFilenameWithPeriod(filename, period) {
    const parts = filename.split('.')
    const namePart = parts[0]
    const extension = parts[1] || 'parquet'
    
    const nameParts = namePart.split('_')
    if (nameParts.length >= 3) {
      nameParts[nameParts.length - 1] = period
      return nameParts.join('_') + '.' + extension
    }
    
    return filename
  },

  async getKlineData(product, contract, resolution, fromTime, toTime) {
    const userId = getCurrentUserId()
    const response = await api.get('/kline', {
      params: {
        product,
        contract,
        resolution,
        from_time: fromTime,
        to_time: toTime,
        user_id: userId
      }
    })
    return response.data
  },

  parseContractName(name) {
    const nameToProduct = {
      '焦煤主连': 'JM',
      '焦炭主连': 'J',
      '螺纹钢主连': 'RB',
      '铁矿石主连': 'I',
      '甲醇主连': 'MA',
      'PTA主连': 'TA',
      '豆粕主连': 'M',
      '豆油主连': 'Y',
      '棕榈油主连': 'P',
      '菜油主连': 'OI',
      '黄金主连': 'AU',
      '白银主连': 'AG',
      '铜主连': 'CU',
      '铝主连': 'AL',
      '锌主连': 'ZN',
      '镍主连': 'NI',
      '锡主连': 'SN',
      '铅主连': 'PB',
      '焦煤': 'JM',
      '焦炭': 'J',
      '螺纹钢': 'RB',
      '铁矿石': 'I',
      '甲醇': 'MA',
      'PTA': 'TA',
      '豆粕': 'M',
      '豆油': 'Y',
      '棕榈油': 'P',
      '菜油': 'OI',
      '黄金': 'AU',
      '白银': 'AG',
      '铜': 'CU',
      '铝': 'AL',
      '锌': 'ZN',
      '镍': 'NI',
      '锡': 'SN',
      '铅': 'PB'
    }
    
    const codeMatch = name.match(/^([A-Za-z]+)(\d{4})$/)
    if (codeMatch) {
      const product = codeMatch[1].toUpperCase()
      const contract = codeMatch[0].toUpperCase()
      return { product, contract }
    }
    
    if (name.includes('-')) {
      const parts = name.split('-')
      const baseName = parts[0].trim()
      const month = parts[1]?.trim() || ''
      
      if (nameToProduct[baseName]) {
        const product = nameToProduct[baseName]
        const contract = month ? `${product}${month}` : product
        return { product, contract }
      }
    }
    
    const numMatch = name.match(/(\d{4})$/)
    if (numMatch) {
      const month = numMatch[1]
      const baseName = name.replace(/-\d{4}$/, '').replace(/\d{4}$/, '')
      if (nameToProduct[baseName]) {
        const product = nameToProduct[baseName]
        return { product, contract: `${product}${month}` }
      }
    }
    
    return { product: name, contract: name }
  },

  async getBars(filename = null, options = {}) {
    const { period = '1d', fromTime = null, toTime = null } = options
    
    if (filename) {
      
      const parts = filename.replace('.parquet', '').split('_')
      if (parts.length >= 2) {
        const exchange = parts[0]
        const rawSymbol = parts[1]
        
        const { product, contract } = this.parseContractName(rawSymbol)
        
        const resolutionMap = {
          '1min': '1',
          '5min': '5',
          '15min': '15',
          '30min': '30',
          '1h': '60',
          '4h': '240',
          '1d': 'D',
          '1w': 'W',
          '1M': 'M'
        }
        
        const resolution = resolutionMap[period] || 'D'
        
        // 如果传入了时间范围，使用传入的时间范围；否则使用默认时间范围
        let queryFromTime = fromTime
        let queryToTime = toTime
        
        if (!queryFromTime || !queryToTime) {
          const now = Math.floor(Date.now() / 1000)
          const timeRangeMap = {
            '1': 60 * 60 * 24 * 3,
            '5': 60 * 60 * 24 * 15,
            '15': 60 * 60 * 24 * 45,
            '30': 60 * 60 * 24 * 90,
            '60': 60 * 60 * 24 * 180,
            '240': 60 * 60 * 24 * 365,
            'D': 60 * 60 * 24 * 365 * 2,
            'W': 60 * 60 * 24 * 365 * 5,
            'M': 60 * 60 * 24 * 365 * 10
          }
          
          queryFromTime = now - (timeRangeMap[resolution] || timeRangeMap['D'])
          queryToTime = now
        }
        
        try {
          const result = await this.getKlineData(product, contract, resolution, queryFromTime, queryToTime)
          
          if (result.s === 'ok') {
            const bars = []
            for (let i = 0; i < result.t.length; i++) {
              bars.push({
                time: result.t[i] * 1000,
                open: result.o[i],
                high: result.h[i],
                low: result.l[i],
                close: result.c[i],
                volume: result.v[i]
              })
            }
            
            return {
              data: bars,
              total: bars.length,
              filename: `${exchange}_${rawSymbol}_${period}.parquet`
            }
          }
        } catch (error) {
          return await this.getDataFile(filename, options)
        }
      }
      
      return await this.getDataFile(filename, options)
    }

    const localData = localStorage.getItem(LOCAL_STORAGE_KEY)
    if (localData) {
      try {
        const parsedData = JSON.parse(localData)
        return parsedData
      } catch (error) {
      }
    }

    try {
      const files = await this.getFileList()
      if (files.files && files.files.length > 0) {
        const firstFile = files.files[0].filename
        return await this.getDataFile(firstFile, options)
      }
    } catch (error) {
    }

    throw new Error('没有可用的数据文件')
  },

  async placeOrder(orderData) {
    const response = await api.post('/order', orderData)
    return response.data
  },

  async closePosition(positionId) {
    const response = await api.post('/close', { position_id: positionId })
    return response.data
  },

  async closeAllPositions() {
    const response = await api.post('/close-all')
    return response.data
  },

  async updatePositionPrice(currentPrice) {
    const response = await api.post('/update-position-price', { current_price: currentPrice })
    return response.data
  },

  async reset() {
    const response = await api.post('/reset')
    return response.data
  },

  async getStatus(currentIndex = 0) {
    const response = await api.get('/status', {
      params: { current_index: currentIndex }
    })
    return response.data
  },

  async getStrategies(userId) {
    const response = await api.get('/strategies', {
      params: { user_id: userId || getCurrentUserId() }
    })
    return response.data
  },

  async createStrategy(strategyData) {
    const response = await api.post('/strategies', strategyData)
    return response.data
  },

  async getStrategyDetail(strategyId) {
    const response = await api.get(`/strategies/${strategyId}`)
    return response.data
  },

  async updateStrategy(strategyId, strategyData) {
    const response = await api.put(`/strategies/${strategyId}`, strategyData)
    return response.data
  },

  async deleteStrategy(strategyId) {
    const response = await api.delete(`/strategies/${strategyId}`)
    return response.data
  },

  async selectStrategy(strategyId) {
    const response = await api.post(`/strategies/${strategyId}/select`)
    return response.data
  },

  async getStrategyEquityCurve(strategyId) {
    const response = await api.get(`/strategies/${strategyId}/equity-curve`)
    return response.data
  },
}
